import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { debug } from '@/utils/debug';
import { hasCreativeStudioAccess } from '@/config/featureAccess';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
  companyName?: string;
  email?: string;
}

export function MobileNav({ isOpen, onClose, userName, companyName, email }: MobileNavProps) {
  const navigate = useNavigate();
  const { logout, memberUuid } = useAuthStore();

  // Handle escape key and body scroll lock
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleLogout = async () => {
    debug.log('MobileNav: Logout clicked');
    onClose();
    await logout();
    navigate('/login');
  };

  const handleSettings = () => {
    debug.log('MobileNav: Settings clicked');
    onClose();
    navigate('/settings');
  };

  const handleNavClick = () => {
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black z-[60] transition-opacity duration-300 ${
          isOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-flouri-darkGray z-[70] transform transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header with close button */}
        <div className="flex justify-between items-start p-4">
          {/* Section 1: User info */}
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-white font-semibold text-lg truncate">{userName || 'User'}</p>
            {companyName && (
              <p className="text-gray-400 text-sm mt-1 truncate">{companyName}</p>
            )}
            {email && (
              <p className="text-gray-500 text-sm mt-1 truncate">{email}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white p-2 hover:bg-gray-700 rounded-lg transition-colors shrink-0"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="mx-4 my-2 border-t border-gray-700" />

        {/* Section 2: Navigation links - ordered to match desktop right-to-left */}
        <nav className="px-4 py-2">
          <Link
            to="/dashboard"
            onClick={handleNavClick}
            className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className="font-medium">Dashboard</span>
          </Link>
          {hasCreativeStudioAccess(memberUuid) && (
            <Link
              to="/creative-studio"
              onClick={handleNavClick}
              className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              <span className="font-medium">Creative Studio</span>
            </Link>
          )}
          <Link
            to="/media-library"
            onClick={handleNavClick}
            className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium">Media Library</span>
          </Link>
          <Link
            to="/account"
            onClick={handleNavClick}
            className="flex items-center space-x-3 px-4 py-3 text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span className="font-medium">Campaigns</span>
          </Link>
        </nav>

        {/* Divider */}
        <div className="mx-4 my-2 border-t border-gray-700" />

        {/* Section 3: Settings & Logout */}
        <div className="px-4 py-2">
          <button
            onClick={handleSettings}
            className="flex items-center space-x-3 w-full px-4 py-3 text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="font-medium">Settings</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-4 py-3 text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="font-medium">Log Out</span>
          </button>
        </div>

        {/* Section 4: Support contact pinned to bottom */}
        <div className="mt-auto px-4 pb-6 pt-4 border-t border-gray-700">
          <p className="text-xs text-white/80 leading-snug text-center">
            Need help? Please contact{' '}
            <a
              href="mailto:support@flouri.ai"
              className="text-white font-semibold underline decoration-white/40 hover:decoration-white"
            >
              support@flouri.ai
            </a>
            .
          </p>
        </div>
      </div>
    </>
  );
}
